(() => {
  if (customElements.get('scroll-animated-image')) {
    return;
  }

  class ScrollAnimatedImage extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.init();
      this.handleIntersection();
      this.attachEvents();
    }

    disconnectedCallback() {
      this.detachEvents();

      this.observer?.unobserve(this);
    }

    attachEvents() {
      this.scrollHandler = () => {
        if (!this.isTicking) {
          this.isTicking = true;

          requestAnimationFrame(() => {
            this.onScroll();
            this.isTicking = false;
          });
        }
      };

      this.debouncedResizeHandler = debounce(this.setupCanvas.bind(this), 200);

      window.addEventListener('scroll', this.scrollHandler);
      window.addEventListener('resize', this.debouncedResizeHandler);
    }

    detachEvents() {
      window.removeEventListener('scroll', this.scrollHandler);
      window.removeEventListener('resize', this.debouncedResizeHandler);
    }

    init() {
      this.canvas = this.querySelector('canvas');
      this.framesData = this.querySelector('[data-frames]')?.textContent;
      this.frames = this.framesData ? JSON.parse(this.framesData) : [];
      this.images = [];
      this.loadedFrames = 0;
      this.currentFrameIndex = -1;
      this.fallback = this.querySelector('[data-fallback-image]');

      this.setupCanvas();
    }

    setupCanvas() {
      if (!this.canvas) {
        return;
      }

      requestAnimationFrame(() => {
        const rect = this.canvas.getBoundingClientRect();
        this.devicePixelRatio = window.devicePixelRatio || 1;
        this.canvasRect = { width: rect.width, height: rect.height };

        this.canvas.width = rect.width * this.devicePixelRatio;
        this.canvas.height = rect.height * this.devicePixelRatio;

        this.ctx = this.canvas.getContext('2d');
        this.ctx.scale(this.devicePixelRatio, this.devicePixelRatio);
        this.ctx.imageSmoothingQuality = 'high';

        if (this.currentFrameIndex >= 0) {
          this.drawFrame(this.currentFrameIndex, true);
        }
      });
    }

    handleIntersection() {
      const handleIntersection = async (entries) => {
        if (!entries[0].isIntersecting) {
          return;
        }

        this.observer.unobserve(this);

        await this.loadFrames();

        this.onScroll();
        this.fallback?.classList.add('hidden');
      };

      this.observer = new IntersectionObserver(handleIntersection.bind(this), {
        rootMargin: '0px 0px 100% 0px',
      });

      this.observer.observe(this);
    }

    async loadFrames() {
      if (!this.frames.length) {
        return;
      }

      const loadImage = src => new Promise(resolve => {
        const img = new Image();

        img.crossOrigin = 'anonymous';
        img.src = src;

        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
      });

      const images = await Promise.all(this.frames.map(loadImage));

      this.images = images.filter(Boolean);
      this.loadedFrames = this.images.length;
    }

    drawFrame(index, force = false) {
      const img = this.images[index];

      if (!img || !this.ctx || (index === this.currentFrameIndex && !force)) {
        return;
      }

      const { width, height } = this.canvasRect || { width: this.canvas.width, height: this.canvas.height };

      this.ctx.clearRect(0, 0, width, height);

      this.ctx.drawImage(img, 0, 0, width, height);

      this.currentFrameIndex = index;
    }

    onScroll() {
      if (!this.frames.length) {
        return;
      }

      const rect = this.getBoundingClientRect();
      const scrollFraction = Math.min(Math.max(1 - rect.top / window.innerHeight, 0), 1);
      const frameIndex = Math.floor(scrollFraction * (this.frames.length - 1));

      requestAnimationFrame(() => this.drawFrame(frameIndex));
    }
  }

  customElements.define('scroll-animated-image', ScrollAnimatedImage);
})();
